import { BaseRepository } from "@/repositories/base-repository"
import {
  type IDraftProductionStatement,
  type IProductionStatement
} from "@/interfaces/api/production-statement.interface"
import type { IProductionStatementCreationJob } from "@/interfaces/api/production-statement-creation-job.interface"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import DataHelper from "@/helper/data-helper"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"

export class ProductionStatementRepository extends BaseRepository<IProductionStatement> {
  protected resourcePath = "administration/production-statements"

  public async getDraftProductionStatements(
    query?: ODataQueryBuilder
  ): Promise<IDraftProductionStatement[]> {
    let request = new HTTPRequestBuilder(HTTPMethods.GET, `${this.resourcePath}/drafts/`)
    if (query) {
      request = request.query(query.build())
    }
    const response = await request.build().execute()

    const data = response.data
    if (Array.isArray(data)) {
      return data as IDraftProductionStatement[]
    }

    if (Array.isArray(data?.results)) {
      return data.results as IDraftProductionStatement[]
    }

    const drafts = DataHelper.denormalizeData(data)
    return Array.isArray(drafts) ? (drafts as IDraftProductionStatement[]) : []
  }

  public async bulkGenerateProductionStatements(
    query?: ODataQueryBuilder
  ): Promise<IProductionStatementCreationJob[]> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/bulk-generate/`
    )
      .setData({ query: query?.build().toString() ?? "" })
      .setExpectedStatusCode(202)
      .build()
      .execute()

    const jobs = DataHelper.denormalizeData(response.data)
    return (Array.isArray(jobs) ? jobs : []) as IProductionStatementCreationJob[]
  }

  public async sendProductionStatement(
    productionStatement: IProductionStatement
  ): Promise<IProductionStatement> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/${productionStatement.id}/send-email/`
    )
      .setExpectedStatusCode(200)
      .build()
      .execute()

    return DataHelper.denormalizeData(response.data) as IProductionStatement
  }

  /**
   * Revert statements (a soft delete, PNTS-3218). The server answers with the reverted statements, so the
   * caller can update its own copies; our own socket message is ignored.
   */
  public async revertProductionStatements(
    ids: string[],
    notifyFinancialOfficer: boolean
  ): Promise<IProductionStatement[]> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/bulk-delete/`
    )
      .setData(ids)
      .query(notifyFinancialOfficer ? { notify_financial_officer: true } : {})
      .setExpectedStatusCode(200)
      .build()
      .execute()

    return DataHelper.denormalizeData(response.data) as IProductionStatement[]
  }

  public async downloadProductionStatements(ids: string[]): Promise<Blob> {
    const response = await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `${this.resourcePath}/bulk-download/`
    )
      .setData(ids)
      .setResponseType("blob")
      .build()
      .execute()

    return response.data as Blob
  }
}
